import React from 'react'
import { Table, Button, Tag, Space, Avatar } from 'antd'
import { EyeOutlined, EditOutlined, DeleteOutlined, FolderOutlined, PictureOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { Category } from '../types/category.types'
import { DocumentAvatar } from '../../../shared/components/DocumentAvatar'

interface CategoriesTableProps {
    categories: Category[]
    onRowClick: (category: Category) => void
    onEdit: (category: Category) => void
    onDelete: (category: Category) => void
}

export const CategoriesTable: React.FC<CategoriesTableProps> = ({
    categories,
    onRowClick,
    onEdit,
    onDelete
}) => {
    const columns: ColumnsType<Category> = [
        {
            title: 'Image',
            dataIndex: 'image',
            key: 'image',
            width: 80,
            render: (image: string, record: Category) => (
                record.imageId ? (
                    <DocumentAvatar documentId={record.imageId} size={50} shape="square" />
                ) : image ? (
                    <Avatar src={image} size={50} shape="square" />
                ) : (
                    <Avatar size={50} shape="square" icon={<PictureOutlined />} style={{ backgroundColor: '#f0f0f0', color: '#999' }} />
                )
            ),
        },
        {
            title: 'Category Name',
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => (
                <Space>
                    <FolderOutlined style={{ color: '#7c3aed' }} />
                    <strong>{name}</strong>
                </Space>
            ),
        },
        {
            title: 'Slug',
            dataIndex: 'slug',
            key: 'slug',
            render: (slug: string) => <Tag>{slug}</Tag>,
        },
        {
            title: 'Parent Category',
            dataIndex: 'parentCategory',
            key: 'parentCategory',
            render: (parent: string) => parent || <Tag color="blue">Root</Tag>,
        },
        {
            title: 'Products',
            dataIndex: 'productCount',
            key: 'productCount',
            render: (count: number) => <Tag color="purple">{count} products</Tag>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'active' ? 'green' : 'gray'}>{(status || 'inactive').toUpperCase()}</Tag>
            ),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => onRowClick(record)}>
                        View
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                            e.stopPropagation()
                            onEdit(record)
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                            e.stopPropagation()
                            onDelete(record)
                        }}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ]

    return (
        <Table
            columns={columns}
            dataSource={categories}
            rowKey="id"
            onRow={(record) => ({
                onClick: () => onRowClick(record),
                style: { cursor: 'pointer' },
            })}
            pagination={{
                total: categories.length,
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} categories`,
            }}
        />
    )
}
